import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, CircularProgress, Container, Divider, Stack, TextField, Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';
import SitePicker from './SitePicker';

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

export default function MeasureFieldDataPage() {
  const { selectedProject, selectedSite, setSelectedSite } = useProject();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [available, setAvailable] = useState(null); // { start, end }
  // Daily granularity (7-day rolling avg context). Hourly view is a backend
  // capability we don't expose in the UI — flux is only meaningful at the
  // daily/weekly scale here.
  const granularity = 'daily';
  const [ts, setTs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset the picker state whenever the site changes — the available range is per-site.
  useEffect(() => {
    setStart(''); setEnd(''); setAvailable(null); setTs(null);
  }, [selectedSite?.id]);

  // Window must be at least this many days — the trailing 7-day window plus
  // enough prior data for a meaningful comparison.
  const MIN_WINDOW_DAYS = 28;

  const reload = useCallback(() => {
    if (!selectedSite?.id) return;
    // Validate window length when both pickers are set. Empty values mean
    // "use server default", which is always >= MIN_WINDOW_DAYS.
    if (start && end) {
      const spanDays = Math.round((new Date(end) - new Date(start)) / 86400000) + 1;
      if (spanDays < MIN_WINDOW_DAYS) {
        setError(`Selected window is ${spanDays} day${spanDays === 1 ? '' : 's'} — minimum is ${MIN_WINDOW_DAYS} days.`);
        return;
      }
    }
    setLoading(true); setError('');
    const qs = new URLSearchParams({ granularity });
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    fetch(`/v1/flux/sites/${selectedSite.id}/timeseries?${qs.toString()}`, { headers: bearerHeaders() })
      .then(r => r.json())
      .then(t => {
        if (!t.ok) throw new Error(t.message);
        setTs(t);
        if (t.available_range) setAvailable(t.available_range);
        // First fetch returns the server-resolved window — pre-fill the pickers
        // so the user can edit from a sensible default.
        if (!start && t.start) setStart(t.start);
        if (!end && t.end) setEnd(t.end);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedSite?.id, granularity, start, end]);

  // Helpers to drive the date inputs' min/max so the user can't pick a < 28-day window.
  const addDays = (iso, n) => {
    if (!iso) return undefined;
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const startMax = end ? addDays(end, -(MIN_WINDOW_DAYS - 1)) : available?.end;
  const endMin = start ? addDays(start, MIN_WINDOW_DAYS - 1) : available?.start;

  useEffect(() => { reload(); }, [reload]);

  // Non-overlapping 7-day calendar buckets, anchored on the visible window's
  // start date. Bucket k spans [start+7k, start+7k+6]. Each row averages the
  // observations whose `date` falls inside its bucket — only data we have, no
  // overlap with adjacent buckets, no interpolation across natural gaps. If a
  // bucket has zero observations, it's skipped (returned only when populated).
  // Per-metric stdev/min/max are over the day-means inside the bucket.
  const bucketed = useCallback(() => {
    const pts = ts?.points || [];
    if (!pts.length || !ts?.start || !ts?.end) return [];
    const dayMs = 86400000;
    const tsOf = (d) => new Date(`${d}T00:00:00Z`).getTime();
    const fmt = (t) => new Date(t).toISOString().slice(0, 10);
    const round3 = (v) => +v.toFixed(3);
    // avg / sample stdev (n-1) / min / max over the day-means within a bucket.
    // n < 2 → stdev = 0 (single observation has no spread).
    const stats = (xs) => {
      if (!xs.length) return { avg: null, stdev: null, min: null, max: null };
      const n = xs.length;
      const m = xs.reduce((a, x) => a + x, 0) / n;
      const s = n > 1 ? Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / (n - 1)) : 0;
      let lo = xs[0]; let hi = xs[0];
      for (const x of xs) { if (x < lo) lo = x; if (x > hi) hi = x; }
      return { avg: round3(m), stdev: round3(s), min: round3(lo), max: round3(hi) };
    };
    const startTs = tsOf(ts.start);
    const endTs = tsOf(ts.end);
    const buckets = [];
    for (let bStart = startTs; bStart <= endTs; bStart += 7 * dayMs) {
      const bEnd = Math.min(bStart + 6 * dayMs, endTs);
      const inBucket = pts.filter(q => {
        const t = tsOf(q.date);
        return t >= bStart && t <= bEnd;
      });
      if (inBucket.length === 0) continue;
      const neeStats = stats(inBucket.map(x => x.nee_gCm2d).filter(Number.isFinite));
      const gppStats = stats(inBucket.map(x => x.gpp_gCm2d).filter(Number.isFinite));
      const recoStats = stats(inBucket.map(x => x.reco_gCm2d).filter(Number.isFinite));
      buckets.push({
        // `date` plotted = bucket start; chart's datetime axis renders it as
        // a position-on-time. For the CSV consumer we expose the full range.
        date: fmt(bStart),
        bucket_start: fmt(bStart),
        bucket_end: fmt(bEnd),
        days_in_bucket: inBucket.length,
        nee_7d_gCm2d: neeStats.avg,
        nee_stdev_gCm2d: neeStats.stdev,
        nee_min_gCm2d: neeStats.min,
        nee_max_gCm2d: neeStats.max,
        gpp_7d_gCm2d: gppStats.avg,
        gpp_stdev_gCm2d: gppStats.stdev,
        gpp_min_gCm2d: gppStats.min,
        gpp_max_gCm2d: gppStats.max,
        reco_7d_gCm2d: recoStats.avg,
        reco_stdev_gCm2d: recoStats.stdev,
        reco_min_gCm2d: recoStats.min,
        reco_max_gCm2d: recoStats.max,
      });
    }
    return buckets;
  }, [ts]);

  const downloadVisibleCsv = useCallback(() => {
    if (!ts?.points?.length) return;
    setError('');
    // One row per non-overlapping 7-day calendar bucket. days_in_bucket says
    // how many of the 7 days had real observations.
    const rows7d = bucketed();
    const header = [
      'bucket_start', 'bucket_end', 'days_in_bucket',
      'nee_7d_avg_gCm2d', 'nee_stdev_gCm2d', 'nee_min_gCm2d', 'nee_max_gCm2d',
      'gpp_7d_avg_gCm2d', 'gpp_stdev_gCm2d', 'gpp_min_gCm2d', 'gpp_max_gCm2d',
      'reco_7d_avg_gCm2d', 'reco_stdev_gCm2d', 'reco_min_gCm2d', 'reco_max_gCm2d',
    ];
    const cell = (v) => (v == null ? '' : v);
    const rows = rows7d.map(p => [
      p.bucket_start, p.bucket_end, cell(p.days_in_bucket),
      cell(p.nee_7d_gCm2d), cell(p.nee_stdev_gCm2d), cell(p.nee_min_gCm2d), cell(p.nee_max_gCm2d),
      cell(p.gpp_7d_gCm2d), cell(p.gpp_stdev_gCm2d), cell(p.gpp_min_gCm2d), cell(p.gpp_max_gCm2d),
      cell(p.reco_7d_gCm2d), cell(p.reco_stdev_gCm2d), cell(p.reco_min_gCm2d), cell(p.reco_max_gCm2d),
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const siteCode = ts.site?.code || selectedSite.code || `site-${selectedSite.id}`;
    a.href = url;
    a.download = `flux_${siteCode}_${ts.start}_${ts.end}_7d-buckets.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }, [ts, selectedSite, bucketed]);

  if (!selectedProject) return <EmptyState message="Pick a project from the header." />;
  if (!selectedSite) return (
    <SitePicker
      project={selectedProject}
      pageTitle="Field Data"
      pageDescription="Net carbon uptake (NEE), gross production (GPP), and respiration (Reco) over time."
      onPickSite={setSelectedSite}
    />
  );

  return (
    <>
      <Helmet><title>Field Data | Measure</title></Helmet>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">{selectedProject.name} / {selectedSite.name}</Typography>
              <Typography variant="h4">Field Data</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Net carbon uptake (NEE) and its components over time. Daytime is photosynthesis (GPP); night is respiration (Reco).
              </Typography>
            </Box>
            <Stack spacing={1} alignItems="flex-end">
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  type="date"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={start}
                  inputProps={{ min: available?.start, max: startMax }}
                  onChange={(e) => setStart(e.target.value)}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={end}
                  inputProps={{ min: endMin, max: available?.end }}
                  onChange={(e) => setEnd(e.target.value)}
                  sx={{ width: 160 }}
                />
              </Stack>
              {available && (
                <Typography variant="caption" color="text.secondary">
                  Data available {available.start} → {available.end} · min window {MIN_WINDOW_DAYS} days
                </Typography>
              )}
            </Stack>
          </Box>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          {ts?.data_source && ts.data_source !== 'synthesized' && (
            <Alert severity="success" icon={<Iconify icon="eva:checkmark-circle-2-fill" />}>
              <Typography variant="body2">
                <strong>Live data:</strong> {ts.data_source}
              </Typography>
            </Alert>
          )}
          {ts?.data_source === 'synthesized' && (
            <Alert severity="info" icon={<Iconify icon="eva:info-fill" />}>
              <Typography variant="body2">
                Showing synthesized data based on the last model run. This site has no live EC tower yet —
                {' '}<strong>KKL / Buster</strong> and <strong>SANTA / Amir</strong> show real Tel-Hai partner data.
              </Typography>
            </Alert>
          )}

          {(() => {
            // Cache the bucketed series once per render so the cards and chart
            // both read the same numbers.
            const buckets = ts ? bucketed() : [];
            const last = buckets[buckets.length - 1] || {};
            return loading || !ts ? <CircularProgress size={24} /> : (
            <>
              {/* Headline numbers — most recent 7-day bucket */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <FluxCard
                  title="Net carbon uptake (NEE)"
                  value={last.nee_7d_gCm2d}
                  unit="g C/m²/day"
                  caption={last.bucket_start ? `7-day avg · ${last.bucket_start} → ${last.bucket_end} · n=${last.days_in_bucket}` : '—'}
                  color="success"
                  hint="Negative = net uptake"
                />
                <FluxCard
                  title="Day uptake (GPP)"
                  value={last.gpp_7d_gCm2d}
                  unit="g C/m²/day"
                  caption="7-day avg · gross primary production"
                  color="info"
                  hint="Photosynthesis only"
                />
                <FluxCard
                  title="Night respiration (Reco)"
                  value={last.reco_7d_gCm2d}
                  unit="g C/m²/day"
                  caption="7-day avg · ecosystem respiration"
                  color="warning"
                  hint="Plants + soil + microbes breathing"
                />
              </Box>

              {/* Time-series chart — non-overlapping 7-day buckets */}
              <Box sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="overline" color="text.secondary">
                  7-day buckets · {ts.start} → {ts.end} ({buckets.length} buckets, {ts.days} days)
                </Typography>
                <FluxChart points={buckets} />
              </Box>

              {/* Methodology */}
              <Box sx={{ p: 2.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="overline" color="text.secondary">How we calculated this</Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <SourceLine label="Daily flux (NEE/GPP/Reco)" detail="SequestOmeter NDIR sensors, 3-min cadence, K-calibrated against EC tower" />
                  <SourceLine label="Day vs night split" detail="PAR threshold (>5 µmol m⁻² s⁻¹) defines daytime; night-only window for Reco" />
                  <SourceLine label="K coefficient" detail="0.42 (R²=0.91, last calibration 6 months ago)" />
                  <SourceLine label="Wind QC" detail="Observations dropped when u* < 0.5 m/s (low turbulence → unreliable K)" />
                </Stack>
              </Box>

              <Divider />

              {/* Download CSV of the visible window */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">Download data package</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="eva:download-fill" />}
                    onClick={downloadVisibleCsv}
                    disabled={!ts?.points?.length}
                  >
                    Download CSV
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Non-overlapping 7-day buckets of NEE/GPP/Reco for {ts.start} → {ts.end}. Each
                  row reports avg / stdev / min / max over the days in its bucket that have real
                  observations; days_in_bucket says how many out of 7.
                </Typography>
              </Box>
            </>
            );
          })()}
        </Stack>
      </Container>
    </>
  );
}

function FluxCard({ title, value, unit, caption, color, hint }) {
  const v = Number.isFinite(value) ? value.toFixed(2) : '—';
  return (
    <Box sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: `${color}.dark` }}>
        {v} <Typography component="span" variant="body2" color="text.secondary">{unit}</Typography>
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{caption}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontStyle: 'italic' }}>{hint}</Typography>
    </Box>
  );
}

function FluxChart({ points }) {
  // points = the bucketed series from the page, one row per non-overlapping
  // 7-day calendar bucket. We plot one marker per bucket.
  const series = [
    { name: 'NEE (7d avg)', data: points.map(p => ({ x: p.date, y: p.nee_7d_gCm2d })) },
    { name: 'GPP (7d avg)', data: points.map(p => ({ x: p.date, y: p.gpp_7d_gCm2d })) },
    { name: 'Reco (7d avg)', data: points.map(p => ({ x: p.date, y: p.reco_7d_gCm2d })) },
  ];
  const options = {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
    stroke: { width: [2.5, 1.5, 1.5], curve: 'straight' },
    markers: { size: 4 },
    colors: ['#2e7d32', '#1976d2', '#ed6c02'],
    xaxis: { type: 'datetime', labels: { datetimeFormatter: { year: 'yyyy', month: 'MMM', day: 'd MMM' } } },
    yaxis: { title: { text: 'g C/m²/day' }, labels: { formatter: v => v.toFixed(1) } },
    legend: { position: 'top', horizontalAlign: 'right' },
    tooltip: { x: { format: 'd MMM yyyy' }, y: { formatter: v => `${v.toFixed(2)} g C/m²/day` } },
    grid: { borderColor: '#e0e0e0', strokeDashArray: 4 },
    annotations: {
      yaxis: [{ y: 0, borderColor: '#999', label: { text: '0', style: { color: '#666', background: '#fff' } } }],
    },
  };
  return <ReactApexChart options={options} series={series} type="line" height={360} />;
}

function SourceLine({ label, detail }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
      <Iconify icon="eva:checkmark-circle-2-fill" sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
      <Box>
        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>{label}: </Typography>
        <Typography variant="body2" component="span" color="text.secondary">{detail}</Typography>
      </Box>
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Container maxWidth="md">
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Field Data</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Container>
  );
}
